<!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <details ref="elDetails">
    <summary
      ref="elSummary"
      class="flex w-full items-center justify-between bg-grey-200 p-4 font-semibold"
      :class="{ 'cursor-pointer': !preventClick }"
      @click.prevent="onClickSummary($event)"
    >
      <span class="typo-h6">
        <slot name="summary" />
      </span>

      <IconChevron
        v-if="!preventClick"
        class="mx-6 w-5 shrink-0 grow-0 transition-transform duration-300 ease-in-out sm:w-6"
        :class="{ 'rotate-180': isOpen }"
      />
    </summary>

    <div ref="elContent">
      <slot name="content" />
    </div>
  </details>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
// https://css-tricks.com/how-to-animate-the-details-element-using-waapi/

// Define props
const props = defineProps({
  durationOpen: {
    type: Number,
    default: 300,
  },
  durationClose: {
    type: Number,
    default: 250,
  },
  preventClick: {
    type: Boolean,
    default: false,
  },
})

// Refs for DOM elements and state
const elDetails = ref(null) // Reference to the <details> element
const elSummary = ref(null) // Reference to the <summary> element
const elContent = ref(null) // Reference to the content inside <details>
const animation = ref(null)
const isClosing = ref(false)
const isExpanding = ref(false)

const isOpen = ref(false)

// Methods
function onClickSummary() {
  if (props.preventClick) return

  if (isClosing.value || !elDetails.value.open) {
    open()
  } else if (isExpanding.value || elDetails.value.open) {
    shrink()
  }
}

function open() {
  // Add an overflow on the <details> to avoid content overflowing
  elDetails.value.style.overflow = 'hidden'
  // Apply a fixed height on the element
  elDetails.value.style.height = `${elDetails.value.offsetHeight}px`
  // Force the [open] attribute on the details element
  elDetails.value.open = true
  isOpen.value = true
  // Wait for the next frame to call the expand function
  window.requestAnimationFrame(() => expand())
}

function expand() {
  // Set the element as "being expanding"
  isExpanding.value = true
  // Get the current fixed height of the element
  const startHeight = `${elDetails.value.offsetHeight}px`
  // Calculate the open height of the element (summary height + content height)
  const endHeight = `${elSummary.value.offsetHeight + elContent.value.offsetHeight}px`

  // If there is already an animation running
  if (animation.value) {
    // Cancel the current animation
    animation.value.cancel()
  }

  // No animation for IOS Safari < 13.4
  if (typeof elDetails.value.animate === 'undefined') {
    elDetails.value.style.height = endHeight
    onAnimationFinish(true)
    return
  }

  // Start a WAAPI animation
  animation.value = elDetails.value.animate(
    // Set the keyframes from the startHeight to endHeight
    { height: [startHeight, endHeight] },
    {
      duration: parseInt(props.durationOpen),
      easing: 'ease-out',
    },
  )
  // When the animation is complete, call onAnimationFinish()
  animation.value.onfinish = () => onAnimationFinish(true)
  // If the animation is cancelled, isExpanding variable is set to false
  animation.value.oncancel = () => (isExpanding.value = false)
}

function shrink() {
  // Add an overflow on the <details> to avoid content overflowing
  elDetails.value.style.overflow = 'hidden'
  // Set the element as "being closed"
  isClosing.value = true
  isOpen.value = false
  // Store the current height of the element
  const startHeight = `${elDetails.value.offsetHeight}px`
  // Calculate the height of the summary
  const endHeight = `${elSummary.value.offsetHeight}px`

  // If there is already an animation running
  if (animation.value) {
    // Cancel the current animation
    animation.value.cancel()
  }

  // No animation for IOS Safari < 13.4
  if (typeof elDetails.value.animate === 'undefined') {
    elDetails.value.style.height = endHeight
    onAnimationFinish(false)
    return
  }

  // Start a WAAPI animation
  animation.value = elDetails.value.animate(
    // Set the keyframes from the startHeight to endHeight
    { height: [startHeight, endHeight] },
    {
      duration: parseInt(props.durationClose),
      easing: 'ease-out',
    },
  )
  // When the animation is complete, call onAnimationFinish()
  animation.value.onfinish = () => onAnimationFinish(false)
  // If the animation is cancelled, isClosing variable is set to false
  animation.value.oncancel = () => (isClosing.value = false)
}

function onAnimationFinish(open) {
  // Set the open attribute based on the parameter
  elDetails.value.open = open
  isOpen.value = open
  // Clear the stored animation
  animation.value = null
  // Reset isClosing & isExpanding
  isClosing.value = false
  isExpanding.value = false
  // Remove the overflow hidden and the fixed height
  elDetails.value.style.height = ''
  elDetails.value.style.overflow = ''
}
</script>

<style scoped>
  details summary::-webkit-details-marker {
    display: none;
  }
</style>
